import { useMemo, useState } from 'react'
import { Plus, Save, Trash2 } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  Textarea,
  useToast,
  type ColumnDef,
  type Row,
} from '@/components/ui'
import {
  useContractGroup,
  useContractGroups,
  useCreateContractGroup,
  useDeleteContractGroup,
  useUpdateContractGroup,
} from '../api/contractsApi.ts'
import type { ContractGroup, CreateContractGroupInput } from '../types.ts'

export function ContractGroupsSection() {
  const { toast } = useToast()
  const { data = [], isLoading, error, refetch } = useContractGroups()
  const create = useCreateContractGroup()
  const del = useDeleteContractGroup()
  const [createOpen, setCreateOpen] = useState(false)
  const [editGroup, setEditGroup] = useState<ContractGroup | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<ContractGroup | null>(null)

  const columns = useMemo<ColumnDef<ContractGroup, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div>
            <p className="text-small font-medium text-gray-900">{row.original.name}</p>
            {row.original.description && (
              <p className="text-caption text-gray-500">{row.original.description}</p>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'contractTypesCount',
        header: 'Vertragstypen',
        size: 130,
        cell: ({ getValue }) => {
          const n = (getValue() as number | undefined) ?? 0
          return (
            <Badge variant={n > 0 ? 'info' : 'neutral'} size="sm">
              {n}
            </Badge>
          )
        },
      },
      {
        accessorKey: 'contractsCount',
        header: 'Verträge',
        size: 100,
        cell: ({ getValue }) => {
          const n = (getValue() as number | undefined) ?? 0
          return (
            <Badge variant={n > 0 ? 'info' : 'neutral'} size="sm">
              {n}
            </Badge>
          )
        },
      },
      {
        accessorKey: 'billingLookaheadDays',
        header: 'Lookahead',
        size: 130,
        cell: ({ getValue }) => {
          const days = getValue() as number
          return days > 0 ? `${days} Tage` : '—'
        },
      },
      {
        accessorKey: 'active',
        header: 'Status',
        size: 90,
        cell: ({ getValue }) => (
          <Badge variant={(getValue() as boolean) ? 'success' : 'neutral'} size="sm">
            {(getValue() as boolean) ? 'Aktiv' : 'Inaktiv'}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 130,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="xs" onClick={() => setEditGroup(row.original)}>
              Bearbeiten
            </Button>
            <IconButton
              icon={<Trash2 className="w-4 h-4" />}
              aria-label="Gruppe löschen"
              variant="ghost"
              size="xs"
              onClick={() => setConfirmDelete(row.original)}
            />
          </div>
        ),
      },
    ],
    [],
  )

  if (isLoading) return <LoadingState label="Lade Vertragsgruppen …" />
  if (error) {
    return (
      <ErrorState
        description="Vertragsgruppen konnten nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-h4 font-semibold text-gray-900">Vertragsgruppen</h3>
            <p className="text-small text-gray-500">
              Eine Vertragsgruppe bündelt Verträge (für Sammelabrechnungen) und/oder Vertragstypen
              (z.B. Anti-Virus, Hosting, Managed Workplace). Lookahead-Tage steuern, wie weit künftige
              Perioden vorgezogen werden dürfen.
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Plus className="w-4 h-4" />}
            onClick={() => setCreateOpen(true)}
          >
            Gruppe anlegen
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {data.length === 0 ? (
          <EmptyState
            title="Noch keine Vertragsgruppen"
            description="Lege eine Gruppe an, um Verträge auf einer Rechnung zu bündeln."
          />
        ) : (
          <DataTable
            columns={columns}
            data={data}
            getRowCanExpand={(row) =>
              ((row.original.contractTypesCount ?? 0) + (row.original.contractsCount ?? 0)) > 0
            }
            renderExpandedRow={(row: Row<ContractGroup>) => (
              <ContractGroupExpandedRow groupId={row.original.id} />
            )}
          />
        )}
      </Card.Body>

      <ContractGroupFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={(input) =>
          create.mutate(input, {
            onSuccess: () => {
              toast({ title: 'Gruppe angelegt', tone: 'success' })
              setCreateOpen(false)
            },
            onError: (err) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
          })
        }
        saving={create.isPending}
      />

      {editGroup && (
        <ContractGroupFormModal
          open={true}
          group={editGroup}
          onOpenChange={(o) => {
            if (!o) setEditGroup(null)
          }}
          onSubmit={() => undefined}
          saving={false}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Vertragsgruppe löschen?"
        message={confirmDelete ? `Gruppe „${confirmDelete.name}" wird gelöscht.` : ''}
        variant="danger"
        confirmLabel="Löschen"
        loading={del.isPending}
        onConfirm={() => {
          if (!confirmDelete) return
          del.mutate(confirmDelete.id, {
            onSuccess: () => {
              toast({ title: 'Gruppe gelöscht', tone: 'success' })
              setConfirmDelete(null)
            },
            onError: (err) => {
              toast({ title: 'Löschen nicht möglich', description: err.message, tone: 'danger' })
              setConfirmDelete(null)
            },
          })
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </Card>
  )
}

function ContractGroupExpandedRow({ groupId }: { groupId: string }) {
  const { data, isLoading, error } = useContractGroup(groupId)
  if (isLoading) return <LoadingState label="Lade Details …" />
  if (error || !data) {
    return (
      <ErrorState
        description="Details konnten nicht geladen werden."
        details={error instanceof Error ? error.message : String(error ?? '')}
      />
    )
  }
  const types = data.contractTypes ?? []
  const linked = data.contracts ?? []
  return (
    <div className="grid gap-4 p-4 md:grid-cols-2">
      <div>
        <p className="text-caption font-medium uppercase tracking-wide text-gray-500">Vertragstypen</p>
        {types.length === 0 ? (
          <p className="mt-1 text-small text-gray-500">Keine Vertragstypen zugeordnet.</p>
        ) : (
          <ul className="mt-1 space-y-1">
            {types.map((t) => (
              <li key={t.id} className="flex items-center gap-2 text-small text-gray-800">
                <span className="font-medium">{t.name}</span>
                {t.sku && <span className="text-caption text-gray-500">({t.sku})</span>}
                {!t.active && (
                  <Badge variant="neutral" size="sm">
                    Inaktiv
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <p className="text-caption font-medium uppercase tracking-wide text-gray-500">Verträge</p>
        {linked.length === 0 ? (
          <p className="mt-1 text-small text-gray-500">Keine Verträge zugeordnet.</p>
        ) : (
          <ul className="mt-1 space-y-1">
            {linked.map((c) => (
              <li key={c.id} className="text-small text-gray-800">
                <span className="font-medium">{c.contractNumber}</span>
                {c.name && <span className="text-gray-500"> — {c.name}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

interface FormModalProps {
  open: boolean
  group?: ContractGroup
  onOpenChange: (o: boolean) => void
  onSubmit: (input: CreateContractGroupInput) => void
  saving: boolean
}

function ContractGroupFormModal({ open, group, onOpenChange, onSubmit, saving }: FormModalProps) {
  const { toast } = useToast()
  const update = useUpdateContractGroup(group?.id ?? '')
  const [name, setName] = useState(group?.name ?? '')
  const [description, setDescription] = useState(group?.description ?? '')
  const [billingLookaheadDays, setBillingLookaheadDays] = useState(String(group?.billingLookaheadDays ?? 0))
  const [active, setActive] = useState(group?.active ?? true)

  const isEdit = Boolean(group)

  function handleSubmit() {
    if (!name.trim()) {
      toast({ title: 'Name fehlt', tone: 'danger' })
      return
    }
    const lookahead = Math.max(0, Math.round(Number(billingLookaheadDays) || 0))
    const payload: CreateContractGroupInput = {
      name: name.trim(),
      description: description || null,
      billingLookaheadDays: lookahead,
      active,
    }
    if (isEdit && group) {
      update.mutate(payload, {
        onSuccess: () => {
          toast({ title: 'Gruppe aktualisiert', tone: 'success' })
          onOpenChange(false)
        },
        onError: (err) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
      })
    } else {
      onSubmit(payload)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Vertragsgruppe bearbeiten' : 'Vertragsgruppe anlegen'}
      size="md"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Save className="w-4 h-4" />}
            onClick={handleSubmit}
            loading={saving || update.isPending}
          >
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
        />
        <Textarea
          label="Beschreibung"
          rows={2}
          value={description ?? ''}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <Input
          label="Lookahead (Tage)"
          hint="Wieviel Tage darf eine zukünftige Periode vorgezogen werden, um sie auf derselben Rechnung wie heute fällige Positionen zu bündeln. 0 = strikt periodengenau."
          type="number"
          value={billingLookaheadDays}
          onChange={(e) => setBillingLookaheadDays(e.currentTarget.value)}
        />
        <label className="flex items-center gap-2 text-small text-gray-700">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.currentTarget.checked)} />
          Aktiv
        </label>
      </div>
    </Modal>
  )
}
